import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function TeamSecondaryNav({
  user,
  view,
  setView,
  activeTeamId,
  setActiveTeamId,
  onCreateTeam,
  isCoach
}) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTeams = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const teamsRef = collection(db, 'admin_teams');
      const snapshot = await getDocs(teamsRef);
      const teamsList = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.ownerUid === user.uid) {
          teamsList.push({ id: doc.id, ...data });
        }
      });

      setTeams(teamsList);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const navItems = [
    { id: 'team-dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'team-roster', label: 'Roster', icon: '👥' },
    { id: 'team-lineups', label: 'Lineups', icon: '🏐' },
    { id: 'team-record-match', label: 'Record', icon: '📝' },
    { id: 'team-stats', label: 'Stats', icon: '📈' },
    { id: 'team-reports', label: 'Reports', icon: '📄', coachProOnly: true },
    { id: 'team-seasons', label: 'Seasons', icon: '🏆' },
    { id: 'team-tournaments', label: 'Tournaments', icon: '🎯' },
    { id: 'team-work-list', label: 'Lists', icon: '📋' }
  ];

  // Only show if we're in a team view
  if (!view.startsWith('team') && view !== 'teams') {
    return null;
  }

  // Hide team selector on teams dashboard view
  const showTeamSelector = view !== 'teams';

  return (
    <div className="team-secondary-nav" style={{
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      position: 'sticky',
      top: '0',
      zIndex: 800
    }}>
      <div className="wrap" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '12px 20px'
      }}>
        {/* Team Selector Row */}
        {showTeamSelector && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: activeTeamId ? '12px' : '0',
            flexWrap: 'wrap'
          }}>
          <span style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#8E8E93',
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
            letterSpacing: '0.02em'
          }}>
            Team:
          </span>
          {loading ? (
            <span style={{ fontSize: '13px', color: '#9ca3af' }}>Loading...</span>
          ) : teams.length === 0 ? (
            <button
              onClick={onCreateTeam}
              style={{
                padding: '8px 16px',
                background: '#007AFF',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                minHeight: '36px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#0051D5';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#007AFF';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              + Create Team
            </button>
          ) : teams.length === 1 ? (
            <>
              <span style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#000000',
                letterSpacing: '-0.02em'
              }}>
                {teams[0].name}
              </span>
              <button
                onClick={onCreateTeam}
                style={{
                  padding: '7px 14px',
                  background: '#F2F2F7',
                  color: '#007AFF',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                  minHeight: '36px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#E5E5EA';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#F2F2F7';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                + New
              </button>
            </>
          ) : (
            <>
              <select
                value={activeTeamId || ''}
                onChange={(e) => {
                  setActiveTeamId(e.target.value);
                  if (e.target.value && view.startsWith('team')) {
                    setView('team-dashboard');
                  }
                }}
                style={{
                  padding: '8px 14px',
                  paddingRight: '38px',
                  border: '1px solid #D1D1D6',
                  borderRadius: '10px',
                  fontSize: '15px',
                  background: 'white',
                  cursor: 'pointer',
                  fontWeight: '400',
                  color: '#000000',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3E%3Cpath fill=\'none\' stroke=\'%23007AFF\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M2 5l6 6 6-6\'/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '16px',
                  minWidth: '160px',
                  minHeight: '36px'
                }}
              >
                <option value="">Select team...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              <button
                onClick={onCreateTeam}
                style={{
                  padding: '7px 14px',
                  background: '#F2F2F7',
                  color: '#007AFF',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                  minHeight: '36px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#E5E5EA';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#F2F2F7';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                + New
              </button>
            </>
          )}
          </div>
        )}

        {/* Navigation Pills - Only show if team is selected */}
        {activeTeamId && (
          <div style={{
            display: 'flex',
            gap: '4px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingBottom: '2px'
          }}>
            <style>
              {`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
            {navItems.filter(item => !item.coachProOnly || isCoach).map(item => {
              const isActive = view === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => setView(item.id)}
                  style={{
                    padding: '7px 12px',
                    background: isActive ? '#E5E5EA' : 'transparent',
                    border: 'none',
                    borderRadius: '10px',
                    color: isActive ? '#000000' : '#8E8E93',
                    fontSize: '13px',
                    fontWeight: isActive ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    flexShrink: 0,
                    minHeight: '34px'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = '#F2F2F7';
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#8E8E93';
                    }
                  }}
                >
                  <span style={{ fontSize: '15px', lineHeight: '1' }}>{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Add media query to hide labels on small screens */}
      <style>
        {`
          @media (max-width: 640px) {
            .nav-label {
              display: none;
            }
          }
        `}
      </style>
    </div>
  );
}
